// @flow
import React from 'react';
import { Field, FieldArray, reduxForm } from 'redux-form';
import { Prompt } from 'react-router-dom';

import StyledForm from '../shared/StyledForm';
import Scrollable from '../shared/Scrollable';
import styled from 'styled-components';

import Button from '../shared/Button';
import MultiS3Upload from '../shared/forms/MultiS3Upload';
import S3Upload from '../shared/forms/S3Upload';
import TextInput from '../shared/forms/TextInput';
import VideoInput from '../shared/forms/VideoInput';

import { colors } from '../../shared/styles';
import { generatePropertyBrochure } from '../../redux/reducers/property';

const StyledSinglePhoto = styled.div`display: inline-block;`;

const BrochureHeader = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
`;

const GenerateBrochureButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    font-size: 13px;
    color: ${colors.orange.default};
    text-decoration: underline;
    cursor: pointer;

    &[disabled] {
        cursor: default;
        opacity: 0.6;
        text-decoration: none;
    }
`;

// Small self-contained link that generates the brochure PDF on the fly and
// opens it in a new tab. Local state drives the transient "Generating" label.
class GenerateBrochureLink extends React.Component {
    state = { generating: false };

    handleClick = () => {
        const { dispatch, propertyReference } = this.props;

        this.setState({ generating: true });

        const done = () => this.setState({ generating: false });
        const result = dispatch(generatePropertyBrochure(propertyReference));

        if (result && typeof result.then === 'function') {
            result.then(done, done);
        } else {
            done();
        }
    };

    render() {
        const { generating } = this.state;

        return (
            <GenerateBrochureButton
                type="button"
                onClick={this.handleClick}
                disabled={generating}
            >
                {generating ? 'Generating PDF…' : 'Generate PDF'}
            </GenerateBrochureButton>
        );
    }
}

export const validate = (values: Object) => {
    const errors = {};

    if (!values.mainPhotoAsset || !values.mainPhotoAsset.assetId) {
        errors.mainPhotoAsset = 'Main photo is required';
    }

    return errors;
};

let PropertyMediaForm = props => {
    const {
        handleSubmit,
        dirty,
        submitting,
        propertyReference,
        dispatch,
    } = props;

    return (
        <StyledForm onSubmit={handleSubmit}>
            <Prompt
                when={dirty && !submitting}
                message="You have made changes that will be lost if you proceed. Are you sure you want to continue?"
            />

            <Scrollable additionalClassName="scroll-area">
                <div className="noflex">
                    <div className="maxWidth">
                        <h4>Main Photo</h4>

                        <StyledSinglePhoto>
                            <Field
                                name="mainPhotoAsset"
                                accept="image/gif, image/jpeg"
                                assetType="property.main.image"
                                maxFileSize={1024 * 1024 * 10}
                                component={S3Upload}
                                disabled={submitting}
                            />
                        </StyledSinglePhoto>
                    </div>

                    <h4>Photo Gallery</h4>

                    <FieldArray
                        addText="Add Photo"
                        name="photos"
                        fieldName="asset"
                        accept="image/gif, image/jpeg"
                        assetType="property.gallery.image"
                        maxFileSize={1024 * 1024 * 10}
                        component={MultiS3Upload}
                        disabled={submitting}
                    />

                    <div className="maxWidth">
                        <BrochureHeader>
                            <h4>Brochure</h4>

                            {propertyReference &&
                                <GenerateBrochureLink
                                    propertyReference={propertyReference}
                                    dispatch={dispatch}
                                />}
                        </BrochureHeader>

                        <Field
                            name="brochureAsset"
                            accept="application/pdf"
                            assetType="property.brochure"
                            maxFileSize={1024 * 1024 * 20}
                            component={S3Upload}
                            disabled={submitting}
                        />
                    </div>

                    <h4>Additional Files</h4>

                    <FieldArray
                        addText="Add Another File"
                        name="additionalFiles"
                        fieldName="asset"
                        accept="application/pdf"
                        assetType="property.additional.file"
                        maxFileSize={1024 * 1024 * 10}
                        component={MultiS3Upload}
                        disabled={submitting}
                    />

                    <div className="maxWidth">
                        <h4>Videos</h4>

                        <FieldArray
                            addText="Add Video"
                            name="videos"
                            component={VideoInput}
                            disabled={submitting}
                        />

                        <h4>Plasma Video</h4>

                        <div className="video-form">
                            <div className="video">
                                <Field
                                    name="plasmaVideoCode"
                                    label="Video Code"
                                    component={TextInput}
                                    disabled={submitting}
                                />

                                <Field
                                    name="plasmaVideoTitle"
                                    label="Video Title"
                                    component={TextInput}
                                    disabled={submitting}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Scrollable>

            <div className="sticky-footer">
                <Button type="submit" disabled={submitting}>
                    {submitting ? 'Saving...' : 'Save & Continue'}
                </Button>
            </div>
        </StyledForm>
    );
};

PropertyMediaForm = reduxForm({
    form: 'propertyEditMedia',
    validate,
})(PropertyMediaForm);

export default PropertyMediaForm;



// WEBPACK FOOTER //
// ./src/components/PropertyForms/PropertyMediaForm.js